import { FC, useMemo, useState, MouseEvent } from 'react';
import styled from 'styled-components';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import { Block, DoneRounded, MoreHoriz } from '@mui/icons-material';
import { IconTransfers } from '@vs-online-only-app/ui';
import { useGetAssociateOutgoingOpenTransferRequests } from '@vs-online-only-app/state';
import { useSelectedHeroConversation } from '../../hooks/useSelectedHeroConversation';
import { SelectedConversationEndConfirmationModal } from './SelectedConversationEndConfirmationModal';
import SelectedConversationTransferModal from '../outgoing-transfer/modal/SelectedConversationTransferModal';
import BlockUserConfirmationModal from './BlockUserConfirmationModal';

const ListItemContent = styled(Stack)`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const SelectedConversationActionsMenu: FC = () => {
  const [isTransferModalOpen, setIsTransferModalOpen] =
    useState<boolean>(false);
  const [isEndChatConfirmationModalOpen, setIsEndChatConfirmationModalOpen] =
    useState<boolean>(false);
  const [isBlockConfirmationModalOpen, setIsBlockConfirmationModalOpen] =
    useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const conversation = useSelectedHeroConversation();

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenTransferModal = () => {
    setIsTransferModalOpen(true);
    closeMenu();
  };

  const { data: openTransferRequests } =
    useGetAssociateOutgoingOpenTransferRequests();

  const hasOngoingTransfer = useMemo(
    () =>
      Boolean(
        openTransferRequests?.find(
          (request) => request.conversation === conversation?.id,
        ),
      ),
    [conversation, openTransferRequests],
  );

  return (
    <>
      <Tooltip title={<Typography>More actions</Typography>} arrow>
        <IconButton onClick={openMenu} id="menu-button">
          <MoreHoriz />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        MenuListProps={{
          'aria-labelledby': 'menu-button',
        }}
      >
        <Tooltip
          placement="left"
          arrow
          title={
            <Typography variant="body2">
              Once you&apos;ve selected a colleague to transfer this chat,
              they&apos;ll receive a request to accept your transfer.
            </Typography>
          }
        >
          <MenuItem onClick={handleOpenTransferModal}>
            <ListItemIcon>
              <IconTransfers />
            </ListItemIcon>
            <ListItemContent>
              <ListItemText>Transfer to someone else</ListItemText>
              {hasOngoingTransfer && (
                <Chip label="Waiting for a response" size="small" />
              )}
            </ListItemContent>
          </MenuItem>
        </Tooltip>
        <Tooltip
          placement="left"
          arrow
          title={
            <Typography variant="body2">
              Once a chat is ended, it&apos;ll be moved to Archived chats and
              can only be restarted by the customer.
            </Typography>
          }
        >
          <MenuItem onClick={() => setIsEndChatConfirmationModalOpen(true)}>
            <ListItemIcon>
              <DoneRounded />
            </ListItemIcon>
            <ListItemText>End chat</ListItemText>
          </MenuItem>
        </Tooltip>
        <MenuItem onClick={() => setIsBlockConfirmationModalOpen(true)}>
          <ListItemIcon>
            <Block />
          </ListItemIcon>
          <ListItemText>Block user</ListItemText>
        </MenuItem>
      </Menu>
      {isTransferModalOpen && (
        <SelectedConversationTransferModal
          onClose={() => setIsTransferModalOpen(false)}
        />
      )}
      {conversation && isBlockConfirmationModalOpen && (
        <BlockUserConfirmationModal
          onClose={() => setIsBlockConfirmationModalOpen(false)}
          conversation={conversation}
        />
      )}
      <SelectedConversationEndConfirmationModal
        isOpen={isEndChatConfirmationModalOpen}
        onClose={() => setIsEndChatConfirmationModalOpen(false)}
      />
    </>
  );
};
